import React, { useState, useContext, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AuthContext, SearchContext } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { LogIn, LogOut, User, Shield, Search } from "lucide-react";
import axios from "axios";
import ForgotPasswordModal from "@/components/ForgotPasswordModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

const Header = () => {
  const { user, login, logout } = useContext(AuthContext);
  const { searchQuery, setSearchQuery } = useContext(SearchContext);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [authEmail, setAuthEmail] = useState("");
  const [authPassword, setAuthPassword] = useState("");
  const [newCouponsCount, setNewCouponsCount] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  // Fetch new coupons count when user changes
  useEffect(() => {
    if (user) {
      fetchNewCouponsCount();
      // Check for new coupons every 30 seconds
      const interval = setInterval(fetchNewCouponsCount, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const fetchNewCouponsCount = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/user/coupons/new/count`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNewCouponsCount(response.data.new_coupons_count);
    } catch (error) {
      console.error("Error fetching new coupons count:", error);
    }
  };

  // Show header on home and on the requested pages
  const showOnPaths = ["/", "/peliculas", "/series", "/novelas"];
  const showOnPathsExtended = [...showOnPaths, "/contenido"];
  const shouldRender = showOnPathsExtended.some((p) => location.pathname === p || location.pathname.startsWith(p + "/"));
  if (!shouldRender) return null;

  const handleAuth = async (isLogin) => {
    try {
      const endpoint = isLogin ? "/auth/login" : "/auth/register";
      const response = await axios.post(`${API}${endpoint}`, {
        email: authEmail,
        password: authPassword,
      });

      login(response.data.access_token, response.data.user);
      setShowAuthModal(false);
      setAuthEmail("");
      setAuthPassword("");
      toast.success(isLogin ? "Inicio de sesión exitoso" : "Registro exitoso");
    } catch (error) {
      const errorDetail = error.response?.data?.detail || "Error de autenticación";
      
      // Handle specific error messages
      if (!isLogin && errorDetail.includes("Email already registered")) {
        toast.error("Este correo ya está registrado. Intenta iniciar sesión o usa otro correo.");
      } else if (errorDetail.includes("Invalid credentials")) {
        toast.error("Correo o contraseña incorrectos");
      } else {
        toast.error(errorDetail);
      }
    }
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="container mx-auto px-4 py-2 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <img
              src={require("@/assets/Logo FastCopy HD.png")}
              alt="FastCopy Logo"
              className="w-8 h-8 object-contain cursor-pointer"
              onClick={() => navigate("/")}
              role="button"
            />
            <h1
              className="hidden sm:inline text-lg font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent cursor-pointer"
              onClick={() => navigate("/")}
            >
              FastCopy
            </h1>
          </div>

          <div className="flex items-center gap-1 sm:gap-2">
            <div className="relative flex items-center">
              {/* Inline search for md+ */}
              <div className="hidden md:block relative">
                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  data-testid="search-input"
                  type="text"
                  placeholder="Buscar..."
                  value={searchQuery}
                  onChange={(e) => {
                    const v = e.target.value;
                    setSearchQuery(v);
                    if (!searchQuery && v.trim() && location.pathname !== '/contenido') {
                      navigate('/contenido');
                    }
                  }}
                  className="pl-8 w-40 sm:w-40 md:w-48 h-8 bg-white/10 border-white/20 text-white placeholder:text-gray-400 text-sm max-w-full"
                />
              </div>

              {/* Mobile: icon that opens a modal with a compact placeholder */}
              <button
                className="md:hidden p-2 rounded hover:bg-white/5"
                aria-label="Abrir búsqueda"
                onClick={() => setShowSearchModal(true)}
              >
                <Search className="w-5 h-5 text-white" />
              </button>
            </div>

            {user ? (
              <>
                {user.role === "admin" && (
                  <Button
                    size="sm"
                    data-testid="admin-panel-button"
                    onClick={() => navigate("/admin")}
                    className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 h-8 px-2 text-sm"
                  >
                    <Shield className="w-4 h-4" />
                    <span className="hidden sm:inline ml-1">Admin</span>
                  </Button>
                )}
                <Button
                  size="sm"
                  data-testid="user-profile-button"
                  onClick={() => navigate("/profile")}
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/10 h-8 px-2 text-sm relative"
                >
                  <User className="w-4 h-4" />
                  <span className="hidden sm:inline ml-1">Perfil</span>
                  {newCouponsCount > 0 && (
                    <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                      {newCouponsCount > 9 ? "9+" : newCouponsCount}
                    </span>
                  )}
                </Button>
                <span className="hidden md:inline text-sm text-white/80 px-2">{user.email}</span>
                <Button
                  size="sm"
                  data-testid="logout-button"
                  onClick={logout}
                  className="border-white/20 text-white hover:bg-white/10 h-8 px-2 text-sm"
                >
                  <LogOut className="w-4 h-4" />
                  <span className="hidden sm:inline ml-1">Salir</span>
                </Button>
              </>
            ) : (
              <Button
                size="sm"
                data-testid="login-button"
                onClick={() => setShowAuthModal(true)}
                className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 h-8 px-3 text-sm"
              >
                <LogIn className="w-4 h-4" />
                <span className="ml-1">Entrar</span>
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Search Modal (mobile) */}
      <Dialog open={showSearchModal} onOpenChange={setShowSearchModal}>
        <DialogContent className="bg-slate-900 border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">Buscar</DialogTitle>
          </DialogHeader>
          <div className="mt-2">
            <Input
              autoFocus
              data-testid="search-input-mobile"
              type="text"
              placeholder="Buscar"
              value={searchQuery}
              onChange={(e) => {
                const v = e.target.value;
                setSearchQuery(v);
                if (!searchQuery && v.trim() && location.pathname !== '/contenido') {
                  navigate('/contenido');
                }
              }}
              className="w-full h-10 bg-white/5 border-white/10 text-white"
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Auth Modal */}
      <Dialog open={showAuthModal} onOpenChange={setShowAuthModal}>
        <DialogContent className="bg-slate-900 border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-center">Bienvenido a FastCopy</DialogTitle>
          </DialogHeader>
          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-white/5">
              <TabsTrigger value="login" data-testid="login-tab">Iniciar Sesión</TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab">Registrarse</TabsTrigger>
            </TabsList>
            <TabsContent value="login" className="space-y-4">
              <Input
                data-testid="login-email-input"
                type="email"
                placeholder="Email"
                value={authEmail}
                onChange={(e) => setAuthEmail(e.target.value)}
                className="bg-white/5 border-white/10 text-white"
              />
              <Input
                data-testid="login-password-input"
                type="password"
                placeholder="Contraseña"
                value={authPassword}
                onChange={(e) => setAuthPassword(e.target.value)}
                className="bg-white/5 border-white/10 text-white"
              />
              <Button
                data-testid="login-submit-button"
                onClick={() => handleAuth(true)}
                className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600"
              >
                Iniciar Sesión
              </Button>
              <button
                onClick={() => {
                  setShowAuthModal(false);
                  setShowForgotPassword(true);
                }}
                className="w-full text-center text-sm text-cyan-400 hover:text-cyan-300 transition-colors"
              >
                ¿Olvidaste la contraseña?
              </button>
            </TabsContent>
            <TabsContent value="register" className="space-y-4">
              <Input
                data-testid="register-email-input"
                type="email"
                placeholder="Email"
                value={authEmail}
                onChange={(e) => setAuthEmail(e.target.value)}
                className="bg-white/5 border-white/10 text-white"
              />
              <Input
                data-testid="register-password-input"
                type="password"
                placeholder="Contraseña"
                value={authPassword}
                onChange={(e) => setAuthPassword(e.target.value)}
                className="bg-white/5 border-white/10 text-white"
              />
              <Button
                data-testid="register-submit-button"
                onClick={() => handleAuth(false)}
                className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600"
              >
                Registrarse
              </Button>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Forgot Password Modal */}
      <ForgotPasswordModal 
        isOpen={showForgotPassword} 
        onClose={() => setShowForgotPassword(false)} 
      />
    </>
  );
};

export default Header;
